import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

import {
  useFilterContext,
  useFilterState,
} from 'src/components/app/context/FilterContext';
import Accordion from 'src/components/common/Accordion';
import AccordionItem from 'src/components/common/Accordion/components/AccordionItem';
import AccordionItemHeader from 'src/components/common/Accordion/components/AccordionItemHeader';
import AccordionItemPanel from 'src/components/common/Accordion/components/AccordionItemPanel';
import { usePageFrame } from 'src/components/common/PageFrame';
import { TreeFilter } from 'src/components/common/TreeFilter';
import { filterSpecs } from 'src/components/userAdmin/UserListPage/filterTree';

import { styles } from 'src/components/common/TreeFilter/index.styles';

interface UserListFilterSelectionProps {}

const UserListFilterSelection: FC<UserListFilterSelectionProps> = () => {
  const { t } = useTranslation('admin');
  const { setShowSidebar } = usePageFrame();
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const applySelectedFilters = () => {
    setAppliedFilterGroups(selectedFilterGroups);

    // Close sidebar, if we are in a mobile view.
    if (!upLg) setShowSidebar(false);
  };

  const {
    filterGroups: appliedFilterGroups,
    setFilterGroups: setAppliedFilterGroups,
  } = useFilterContext();

  const selectedFilterState = useFilterState({
    initialFilterGroups: appliedFilterGroups,
    initialFilterSpecs: filterSpecs,
    allowCombinedFilters: true,
  });

  const {
    filterGroups: selectedFilterGroups,
    setFilterGroups: setSelectedFilterGroups,
  } = selectedFilterState;

  useEffect(() => {
    setSelectedFilterGroups(appliedFilterGroups);
  }, [appliedFilterGroups]);

  return (
    <Accordion
      className="UserListFilterSelection-root"
      initiallyActiveItems={['UserListFilterSelection-filters']}
    >
      <AccordionItem
        sx={styles.accordionItem}
        itemName="UserListFilterSelection-filters"
      >
        <AccordionItemHeader headline={t('admin:List.FilterHeading')} />
        <AccordionItemPanel fullHeight>
          <TreeFilter
            filterState={selectedFilterState}
            applyFilter={applySelectedFilters}
            removeFilter={() => setSelectedFilterGroups({})}
          />
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default UserListFilterSelection;
